import { useEffect, useState, useRef } from 'react';
import AppLogo from '../shared/AppLogo.jsx';
import { getDomainBranding } from '../../tenant/tenantConfig.js';
import {
  readPortalTokenFromFragment,
  missingPortalTokenMessage,
} from '../../utils/portalLinkToken.js';

const PORTAL_THEME = {
  '--bg': '#f1f5f9',
  '--bg2': '#e8eef5',
  '--card': '#ffffff',
  '--fg': '#0f172a',
  '--fg2': '#1e293b',
  '--fg3': '#64748b',
  '--fg4': '#94a3b8',
  '--border': '#e2e8f0',
  '--accent': '#0284c7',
  '--accent-ink': '#ffffff',
  '--accent-bg': 'rgba(2,132,199,0.08)',
  '--accent-border': 'rgba(2,132,199,0.18)',
  '--accent-border2': 'rgba(2,132,199,0.12)',
  '--accent-border3': 'rgba(2,132,199,0.2)',
  '--accent-border4': 'rgba(2,132,199,0.22)',
  '--accent-subtle': 'rgba(2,132,199,0.05)',
  '--accent-rgb': '2 132 199',
  '--bg-inset': 'rgba(241,245,249,0.6)',
  '--bg-inset2': 'rgba(226,232,240,0.7)',
  '--danger': '#ef4444',
  '--danger-bg': 'rgba(239,68,68,0.08)',
};

/**
 * Magic link verification page.
 * Mounted at /portal-verify?token=xxx — consumes the token and logs the employee in.
 * On success, opens the employee portal modal automatically.
 */
export default function EmployeeMagicLinkVerify({ verifyMagicLink, onVerified }) {
  const [status, setStatus] = useState('verifying'); // verifying | success | error
  const [error, setError] = useState('');
  const attempted = useRef(false);

  useEffect(() => {
    if (attempted.current) return;
    attempted.current = true;

    // GH #6767 — the token is the credential, so it travels in the fragment
    // and is scrubbed from the address bar before anything else happens.
    // `?token=` is deliberately not read: see portalLinkToken.js.
    const token = readPortalTokenFromFragment();

    if (!token) {
      setStatus('error');
      setError(missingPortalTokenMessage('magic link'));
      return;
    }

    (async () => {
      const result = await verifyMagicLink(token);

      if (!result.ok) {
        setStatus('error');
        setError(result.error);
        return;
      }

      // Redirect to the portal hub — replaceState prevents the verify URL
      // from appearing in history so the back button doesn't re-trigger it.
      window.location.replace('/portal');
    })();
  }, [verifyMagicLink, onVerified]);

  if (status === 'verifying') {
    return (
      <div
        style={{
          ...PORTAL_THEME,
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'var(--bg)',
          fontFamily: 'var(--font-body)',
        }}
      >
        <div style={{ textAlign: 'center' }}>
          <div
            style={{
              width: 52,
              height: 52,
              borderRadius: 14,
              background: 'linear-gradient(135deg, var(--accent), #38bdf8)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 22,
              fontWeight: 800,
              color: '#0d1f1e',
              fontFamily: 'var(--font-display)',
              margin: '0 auto 20px',
            }}
          >
            C
          </div>
          <div style={{ fontSize: 14, color: 'var(--fg3)', fontFamily: 'var(--font-mono)' }}>
            Verifying your magic link...
          </div>
        </div>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div
        style={{
          ...PORTAL_THEME,
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'var(--bg)',
          fontFamily: 'var(--font-body)',
        }}
      >
        <div style={{ width: '100%', maxWidth: 400, padding: '0 24px' }}>
          <div style={{ textAlign: 'center', marginBottom: 40 }}>
            <AppLogo size={52} borderRadius={14} style={{ margin: '0 auto 16px' }} />
            <div className="display" style={{ fontSize: 26, fontWeight: 600, color: 'var(--fg)' }}>
              {getDomainBranding().name}
            </div>
            <div
              style={{
                fontSize: 12,
                color: 'var(--fg3)',
                marginTop: 4,
                fontFamily: 'var(--font-mono)',
              }}
            >
              Employee Portal
            </div>
          </div>

          <div className="card" style={{ padding: 32, textAlign: 'center' }}>
            <div
              style={{
                width: 48,
                height: 48,
                borderRadius: 12,
                background: 'rgba(251, 113, 133, 0.1)',
                border: '1px solid rgba(251, 113, 133, 0.2)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 22,
                margin: '0 auto 20px',
              }}
            >
              &#10007;
            </div>
            <div style={{ fontSize: 16, fontWeight: 600, color: 'var(--fg)', marginBottom: 12 }}>
              Magic link failed
            </div>
            <div style={{ fontSize: 13, color: 'var(--fg3)', lineHeight: 1.6, marginBottom: 24 }}>
              {error}
            </div>

            <a
              href="/portal-login"
              className="btn btn-primary"
              style={{
                display: 'inline-flex',
                justifyContent: 'center',
                padding: '10px 24px',
                fontSize: 13,
                textDecoration: 'none',
              }}
            >
              Request a new magic link
            </a>
          </div>
        </div>
      </div>
    );
  }

  // Success state — briefly shown before the portal modal opens
  return (
    <div
      style={{
        ...PORTAL_THEME,
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'var(--bg)',
        fontFamily: 'var(--font-body)',
      }}
    >
      <div style={{ textAlign: 'center' }}>
        <div
          style={{
            width: 52,
            height: 52,
            borderRadius: 14,
            background: 'linear-gradient(135deg, var(--accent), #38bdf8)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 22,
            fontWeight: 800,
            color: '#0d1f1e',
            fontFamily: 'var(--font-display)',
            margin: '0 auto 20px',
          }}
        >
          C
        </div>
        <div style={{ fontSize: 14, color: 'var(--accent)', fontFamily: 'var(--font-mono)' }}>
          Signed in successfully. Opening your portal...
        </div>
      </div>
    </div>
  );
}
